import { useCallback, useEffect, useState } from "react";
import api, { isVersionConflict } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import DeleteWithReasonDialog from "../DeleteWithReasonDialog";
import { missionErrorMessage } from "../../lib/missions";
import { LEVEL_CODES, isExpectedSkillsValid } from "../../lib/skills";
import { StatePill } from "../CollaboratorBits";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Skeleton } from "../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { AlertCircle, Loader2, Pencil, Plus, Power, Trash2, X } from "lucide-react";

const EMPTY = { label: "", description: "", duration: "", required_skills: [], procedures: [], report_template_id: "", report_validation_required: false };
const STANDARD = "__standard__";

export function useMissionTypes(enabled = true) {
  const [types, setTypes] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const reload = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/mission-types");
      setTypes(data.items);
      setStatus("ready");
    } catch (e) {
      setStatus("error");
    }
  }, [enabled]);
  useEffect(() => { reload(); }, [reload]);
  return { types, status, reload };
}

function durationText(minutes) {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} h${m ? ` ${String(m).padStart(2, "0")}` : ""}` : `${m} min`;
}

// The PATCH body: only what changed, with the expected version.
function buildTypePatch(type, draft) {
  const body = { expected_version: type.version };
  if (draft.label.trim() !== type.label) body.label = draft.label.trim();
  const description = draft.description.trim() || null;
  if (description !== (type.description || null)) body.description = description;
  const duration = draft.duration ? Number(draft.duration) : null;
  if (duration !== (type.default_duration_minutes || null)) body.default_duration_minutes = duration;
  const required = draft.required_skills.map((s) => ({ skill_id: s.skill_id, min_level: s.level }));
  const before = (type.required_skills || []).map((s) => ({ skill_id: s.skill_id, min_level: s.min_level }));
  if (JSON.stringify(required) !== JSON.stringify(before)) body.required_skills = required;
  const procedures = (draft.procedures || []).map((p) => ({ template_id: p.template_id, required: !!p.required }));
  const procBefore = (type.procedures || []).map((p) => ({ template_id: p.template_id, required: !!p.required }));
  if (JSON.stringify(procedures) !== JSON.stringify(procBefore)) body.procedures = procedures;
  if ((draft.report_template_id || null) !== (type.report_template_id || null)) body.report_template_id = draft.report_template_id || null;
  if (!!draft.report_validation_required !== !!type.report_validation_required) body.report_validation_required = !!draft.report_validation_required;
  return body;
}

// Organisation › Types de mission. A mission keeps a snapshot of its type: editing a type never rewrites
// missions already created. Types are deactivated, never deleted.
export default function MissionTypesPanel({ skillRefs, canManage }) {
  const { user } = useAuth();
  const proceduresOn = !!user?.features?.procedures;
  const { types, status, reload } = useMissionTypes();
  const [procedures, setProcedures] = useState([]); // published procedures that can be attached
  useEffect(() => {
    if (!proceduresOn || !canManage) return;
    api.get("/procedures").then(({ data }) => setProcedures(data.items.filter((p) => p.active && p.current_version))).catch(() => setProcedures([]));
  }, [proceduresOn, canManage]);
  const [reportTemplates, setReportTemplates] = useState([]); // published report templates (Ordre 15)
  useEffect(() => {
    if (!canManage) return;
    api.get("/report-templates").then(({ data }) => setReportTemplates(data.items)).catch(() => setReportTemplates([]));
  }, [canManage]);
  const [editing, setEditing] = useState(null); // null | "new" | type
  const [draft, setDraft] = useState(EMPTY);
  const [toggling, setToggling] = useState(null);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = (type) => {
    setEditing(type);
    setDraft(type === "new" ? EMPTY : {
      label: type.label, description: type.description || "", duration: type.default_duration_minutes ? String(type.default_duration_minutes) : "",
      required_skills: (type.required_skills || []).map((s) => ({ skill_id: s.skill_id, level: s.min_level })),
      procedures: (type.procedures || []).map((p) => ({ template_id: p.template_id, required: p.required, label: p.label })),
      report_template_id: type.report_template_id || "", report_validation_required: !!type.report_validation_required,
    });
  };

  const run = async (request, success) => {
    setBusy(true);
    try {
      await request();
      toast.success(success);
      await reload();
      return true;
    } catch (error) {
      toast.error(missionErrorMessage(error));
      if (isVersionConflict(error)) await reload();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const durationValid = !draft.duration || (/^\d+$/.test(draft.duration) && Number(draft.duration) >= 15 && Number(draft.duration) <= 44640);
  const procIds = draft.procedures.map((p) => p.template_id);
  const proceduresValid = procIds.every(Boolean) && new Set(procIds).size === procIds.length;
  const valid = draft.label.trim() && durationValid && isExpectedSkillsValid(draft.required_skills) && proceduresValid;
  const save = async () => {
    const ok = editing === "new"
      ? await run(() => api.post("/mission-types", {
        label: draft.label.trim(), description: draft.description.trim() || null, default_duration_minutes: draft.duration ? Number(draft.duration) : null,
        required_skills: draft.required_skills.map((s) => ({ skill_id: s.skill_id, min_level: s.level })),
        ...(draft.procedures.length ? { procedures: draft.procedures.map((p) => ({ template_id: p.template_id, required: !!p.required })) } : {}),
        ...(draft.report_template_id ? { report_template_id: draft.report_template_id } : {}),
        report_validation_required: !!draft.report_validation_required,
      }), "Type de mission créé")
      : await run(() => api.patch(`/mission-types/${editing.id}`, buildTypePatch(editing, draft)), "Type de mission mis à jour");
    if (ok) setEditing(null);
  };
  const confirmToggle = async () => {
    const ok = await run(() => api.patch(`/mission-types/${toggling.id}`, { expected_version: toggling.version, active: !toggling.active }),
      toggling.active ? "Type désactivé" : "Type réactivé");
    if (ok) setToggleOpen(false);
  };

  const setRequired = (index, patch) => setDraft((d) => ({ ...d, required_skills: d.required_skills.map((e, i) => (i === index ? { ...e, ...patch } : e)) }));
  const skillChoices = (current) => skillRefs.skills.filter((s) => s.active || s.id === current);

  if (status === "loading") return <Skeleton className="h-40 w-full" />;
  if (status === "error") {
    return (
      <Card className="p-8 text-center space-y-3" data-testid="mission-types-error">
        <AlertCircle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-600">Impossible de charger les types de mission. Réessayez dans quelques instants.</p>
        <Button variant="outline" onClick={reload}>Réessayer</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="mission-types-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Les interventions faites dans les pharmacies. Une mission garde le type tel qu'il était à sa création.</p>
        {canManage && (
          <Button onClick={() => open("new")} className={`shrink-0`} data-testid="btn-mission-type-new"><Plus size={16} className="mr-1.5" /> Nouveau type</Button>
        )}
      </div>
      {types.length === 0 ? (
        <Card className="p-8 text-center space-y-2" data-testid="mission-types-empty">
          <p className="font-medium text-slate-900">Aucun type de mission pour le moment</p>
          <p className="text-sm text-slate-500">Créez par exemple « Installation », « Formation » ou « Visite de démarrage », avec les compétences attendues.</p>
        </Card>
      ) : (
        <Card className="p-0 divide-y divide-slate-100">
          {types.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-3 p-4" data-testid={`mission-type-row-${t.code}`}>
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-slate-900 break-words">{t.label}</span><StatePill active={t.active} /></div>
                {t.description && <p className="text-xs text-slate-600 break-words">{t.description}</p>}
                <div className="text-xs text-slate-500">
                  {durationText(t.default_duration_minutes) ? `Durée indicative : ${durationText(t.default_duration_minutes)} · ` : ""}
                  {t.required_skills.length === 0 ? "Aucune compétence attendue" : t.required_skills.map((s) => `${s.label} (${skillRefs.levelLabel(s.min_level)})`).join(" · ")}
                </div>
                {(t.procedures || []).length > 0 && (
                  <div className="text-xs text-slate-500">Procédures : {t.procedures.map((p) => `${p.label}${p.required ? " (obligatoire)" : ""}`).join(" · ")}</div>
                )}
                <div className="text-xs text-slate-500" data-testid={`mission-type-report-${t.code}`}>
                  Compte rendu : {reportTemplates.find((r) => r.id === t.report_template_id)?.label || (t.report_template_id ? "modèle choisi" : "modèle standard")}
                  {t.report_validation_required ? " · à faire valider" : ""}
                </div>
              </div>
              {canManage && (
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" aria-label={`Modifier ${t.label}`} onClick={() => open(t)} data-testid={`btn-mission-type-edit-${t.code}`}><Pencil size={15} /></Button>
                  <Button variant="ghost" size="icon" aria-label={`${t.active ? "Désactiver" : "Réactiver"} ${t.label}`} onClick={() => { setToggling(t); setToggleOpen(true); }}
                    data-testid={`btn-mission-type-toggle-${t.code}`}><Power size={15} /></Button>
                  {hasPermission(user, "missions:delete") && (
                    <Button variant="ghost" size="icon" aria-label={`Supprimer ${t.label}`} onClick={() => setDeleting(t)}
                      data-testid={`btn-mission-type-delete-${t.code}`}><Trash2 size={15} /></Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="mission-type-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Nouveau type de mission" : "Modifier le type de mission"}</DialogTitle>
            {editing !== "new" && <DialogDescription>Les missions déjà créées gardent l'ancienne version de ce type.</DialogDescription>}
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label htmlFor="mt-label">Libellé *</Label>
              <Input id="mt-label" className="text-base md:text-sm" maxLength={120} value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="input-mission-type-label" /></div>
            <div className="space-y-2"><Label htmlFor="mt-desc">Description</Label>
              <Textarea id="mt-desc" rows={2} maxLength={2000} className="text-base md:text-sm" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="mt-duration">Durée indicative (minutes)</Label>
              <Input id="mt-duration" inputMode="numeric" className="text-base md:text-sm w-40" placeholder="ex. 180" value={draft.duration}
                onChange={(e) => setDraft({ ...draft, duration: e.target.value.replace(/\D/g, "") })} data-testid="input-mission-type-duration" />
              <p className="text-xs text-slate-500">Sert à proposer l'heure de fin d'une nouvelle mission.</p>
              {!durationValid && <p className="text-xs text-amber-700">Entre 15 minutes et 31 jours.</p>}
            </div>
            <div className="space-y-2">
              <Label>Compétences attendues</Label>
              <p className="text-xs text-slate-500">À l'affectation, Jamora signale une personne qui ne les a pas le jour de la mission (sans la bloquer).</p>
              {draft.required_skills.map((item, index) => (
                <div key={index} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_9rem_auto] gap-2 items-center min-w-0" data-testid="mission-type-skill-row">
                  <Select value={item.skill_id || undefined} onValueChange={(v) => setRequired(index, { skill_id: v })}>
                    <SelectTrigger className="col-span-2 sm:col-span-1 min-w-0" aria-label="Compétence" data-testid={`select-mission-type-skill-${index}`}><SelectValue placeholder="Choisir une compétence" /></SelectTrigger>
                    <SelectContent>{skillChoices(item.skill_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}{s.active ? "" : " (inactive)"}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={item.level} onValueChange={(v) => setRequired(index, { level: v })}>
                    <SelectTrigger className="min-w-0" aria-label="Niveau minimum"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEVEL_CODES.map((code) => <SelectItem key={code} value={code}>{skillRefs.levelLabel(code)}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button type="button" variant="ghost" size="icon" aria-label="Retirer cette compétence"
                    onClick={() => setDraft((d) => ({ ...d, required_skills: d.required_skills.filter((_, i) => i !== index) }))}><X size={15} /></Button>
                </div>
              ))}
              {skillRefs.skills.some((s) => s.active) ? (
                <Button type="button" variant="outline" size="sm" data-testid="btn-mission-type-skill-add"
                  onClick={() => setDraft((d) => ({ ...d, required_skills: [...d.required_skills, { skill_id: "", level: "autonomous" }] }))}>
                  <Plus size={14} className="mr-1.5" /> Ajouter une compétence
                </Button>
              ) : <p className="text-xs text-slate-500">Créez d'abord des compétences dans l'onglet « Compétences ».</p>}
              {!isExpectedSkillsValid(draft.required_skills) && <p className="text-xs text-amber-700">Choisissez chaque compétence une seule fois.</p>}
            </div>
            {proceduresOn && (
              <div className="space-y-2" data-testid="mission-type-procedures">
                <Label>Procédures à suivre sur place</Label>
                <p className="text-xs text-slate-500">Une procédure obligatoire doit être terminée avant de clore la mission (sinon, un motif est demandé).</p>
                {draft.procedures.map((p, index) => {
                  const choices = procedures.filter((c) => c.id === p.template_id || !procIds.includes(c.id));
                  return (
                    <div key={index} className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2 items-center min-w-0" data-testid="mission-type-procedure-row">
                      <Select value={p.template_id || undefined} onValueChange={(v) => setDraft((d) => ({ ...d, procedures: d.procedures.map((x, i) => (i === index ? { ...x, template_id: v } : x)) }))}>
                        <SelectTrigger className="min-w-0" aria-label="Procédure" data-testid={`select-mission-type-procedure-${index}`}><SelectValue placeholder="Choisir une procédure" /></SelectTrigger>
                        <SelectContent>
                          {choices.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                          {p.template_id && !procedures.some((c) => c.id === p.template_id) && <SelectItem value={p.template_id}>{p.label || "Procédure indisponible"}</SelectItem>}
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-1.5 text-xs text-slate-600">
                        <Switch checked={!!p.required} onCheckedChange={(v) => setDraft((d) => ({ ...d, procedures: d.procedures.map((x, i) => (i === index ? { ...x, required: v } : x)) }))}
                          data-testid={`switch-mission-type-procedure-required-${index}`} />obligatoire
                      </label>
                      <Button type="button" variant="ghost" size="icon" aria-label="Retirer cette procédure"
                        onClick={() => setDraft((d) => ({ ...d, procedures: d.procedures.filter((_, i) => i !== index) }))}><X size={15} /></Button>
                    </div>
                  );
                })}
                {procedures.some((c) => !procIds.includes(c.id)) ? (
                  <Button type="button" variant="outline" size="sm" data-testid="btn-mission-type-procedure-add"
                    onClick={() => setDraft((d) => ({ ...d, procedures: [...d.procedures, { template_id: "", required: true }] }))}>
                    <Plus size={14} className="mr-1.5" /> Ajouter une procédure
                  </Button>
                ) : procedures.length === 0 && <p className="text-xs text-slate-500">Aucune procédure publiée pour le moment.</p>}
              </div>
            )}
            <div className="space-y-2" data-testid="mission-type-report">
              <Label>Compte rendu de fin de mission</Label>
              <Select value={draft.report_template_id || STANDARD} onValueChange={(v) => setDraft((d) => ({ ...d, report_template_id: v === STANDARD ? "" : v }))}>
                <SelectTrigger className="min-w-0" aria-label="Modèle de compte rendu" data-testid="select-mission-type-report-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={STANDARD}>Modèle standard</SelectItem>
                  {reportTemplates.filter((r) => !r.standard && r.active && r.current_version).map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                  {draft.report_template_id && !reportTemplates.some((r) => r.id === draft.report_template_id && r.active && r.current_version) && (
                    <SelectItem value={draft.report_template_id}>Modèle indisponible</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Switch checked={!!draft.report_validation_required} onCheckedChange={(v) => setDraft((d) => ({ ...d, report_validation_required: v }))}
                  data-testid="switch-mission-type-report-validation" />Le compte rendu doit être validé par un responsable
              </label>
              <p className="text-xs text-slate-500">Sans validation, il est publié dès que l'équipe le soumet.</p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Annuler</Button>
            <Button onClick={save} disabled={busy || !valid}  data-testid="btn-mission-type-save">
              {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteWithReasonDialog
        target={deleting} testId="delete-mission-type" onClose={() => setDeleting(null)}
        title={`Supprimer « ${deleting?.label || ""} » ?`}
        description="Aucune mission n'a été créée depuis ce type. Il va dans la corbeille : vous pourrez le restaurer pendant 30 jours."
        onConfirm={async (reason) => {
          const target = deleting;
          setDeleting(null);
          await run(() => api.delete(`/mission-types/${target.id}`, { data: { reason } }), "Type mis à la corbeille");
        }}
      />
      <Dialog open={toggleOpen} onOpenChange={setToggleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{toggling?.active ? `Désactiver « ${toggling?.label} » ?` : `Réactiver « ${toggling?.label} » ?`}</DialogTitle>
            <DialogDescription>
              {toggling?.active ? "Les missions existantes le gardent, mais il ne sera plus proposé pour de nouvelles missions. Vous pourrez le réactiver." : "Il sera de nouveau proposé pour les nouvelles missions."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setToggleOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={confirmToggle} disabled={busy}  data-testid="btn-mission-type-toggle-confirm">
              {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} {toggling?.active ? "Désactiver" : "Réactiver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
